/**
 * Componente que representa o rodapé do OrderDetails
 * 
 * Decisões de UI/UX:
 * - Agrupamento lógico de ações
 * - Mensagens de erro compactas
 * - Métricas em cards eficientes
 * - Layout responsivo otimizado para 285px
 */

import React from 'react';
import { 
  AlertTriangleIcon as AlertTriangle,
  LayersIcon as Layers,
} from '../../icons';
import { Button, InlineAlert } from '../../ui';
import { sendToTerminal } from '../../../lib/terminalLogger';
import { SaveValidationState } from '../types/saveValidation';
// Ícones de métricas substituídos por shapes simples para reduzir dependências
const Box = (props: React.SVGProps<SVGSVGElement>) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/></svg>
);
const MoveVertical = (props: React.SVGProps<SVGSVGElement>) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}><polyline points="8 18 12 22 16 18"/><polyline points="8 6 12 2 16 6"/><line x1="12" y1="2" x2="12" y2="22"/></svg>
);
const Ruler = (props: React.SVGProps<SVGSVGElement>) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}><path d="M16 2l6 6L8 22l-6-6L16 2"/><path d="M7.5 10.5l2 2"/><path d="M10.5 7.5l2 2"/><path d="M13.5 4.5l2 2"/></svg>
);
const Save = (props: React.SVGProps<SVGSVGElement>) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}><path d="M7 21h10a2 2 0 0 0 2-2V7l-4-4H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2z"/><polyline points="7 3 7 8 15 8"/></svg>
);
const Download = (props: React.SVGProps<SVGSVGElement>) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
);
const Info = (props: React.SVGProps<SVGSVGElement>) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
);
const FileText = (props: React.SVGProps<SVGSVGElement>) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>
);

interface FooterSectionProps {
  totalObjectsWithQuantity: number;
  metrosVendidos: number;
  totalAreaWithQuantity: number;
  isMetros: boolean;
  hasExceededMeterage: boolean;
  hasErrors: boolean;
  hasSaveErrors: boolean;
  hasBlocking?: boolean;  // ✅ Objetos excedem largura máxima da regra
  metrageExcess: number;
  saveValidationState: SaveValidationState;
  saveValidationMessage: string | null;
  role: string;
  /** Role efetiva para Salvar/Exportar (Save Temp força fluxo de vendedor) */
  actionRole?: string;
  openOnlyFlow?: boolean;
  userName: string;
  userType: string;
  roleColors: {
    primary: string;
    hover: string;
    text: string;
  };
  onAction: () => void;
  onShowDebug?: () => void;
  /** Abre a modal "Detalhes do pedido" (OrcDetailsModal) com item mãe, observações, valores, etc. */
  onShowOrderDetails?: () => void;
  reorderMode?: boolean;
  onRefuse?: () => void;
  disableSoldMeter?: boolean;
}

export const FooterSection: React.FC<FooterSectionProps> = ({
  totalObjectsWithQuantity,
  metrosVendidos,
  totalAreaWithQuantity,
  isMetros,
  hasExceededMeterage,
  hasErrors,
  hasSaveErrors,
  hasBlocking,
  metrageExcess,
  saveValidationState,
  saveValidationMessage,
  role,
  actionRole,
  openOnlyFlow = false,
  userName,
  userType,
  roleColors,
  onAction,
  onShowDebug,
  onShowOrderDetails,
  reorderMode,
  onRefuse,
  disableSoldMeter = false
}) => {
  const effectiveActionRole = actionRole ?? role;
  const unit = isMetros ? 'm' : 'un';
  const formattedTotal = isMetros ? 
    Math.ceil(totalAreaWithQuantity * 100) / 100 : 
    Math.round(totalAreaWithQuantity);
  const saveDisabled =
    saveValidationState.phase !== 'valid' ||
    !!reorderMode ||
    hasSaveErrors;
  const saveLabel = saveValidationState.phase === 'pending' || saveValidationState.phase === 'idle'
    ? 'Validando'
    : effectiveActionRole === 'producao'
      ? (openOnlyFlow ? 'Abrir arquivo' : 'Exportar')
      : 'Salvar';
  const saveTitle = saveDisabled
    ? (
      reorderMode
        ? 'Finalize a reordenação para continuar'
        : hasSaveErrors
          ? 'Dimensões excedem o máximo permitido para este serviço.'
          : saveValidationMessage || (saveValidationState.phase === 'idle' ? 'Validando pedido...' : 'Não foi possível validar o pedido.')
    )
    : undefined;
  const hasBackendWarning =
    saveValidationState.phase === 'valid' &&
    saveValidationState.result.status === 'warning';
  const showLocalWarning =
    saveValidationState.phase === 'valid' &&
    !hasSaveErrors &&
    (hasErrors || hasExceededMeterage || hasBlocking);
  const inlineAlertType: 'error' | 'info' | 'warning' | null =
    saveValidationState.phase === 'invalid' || saveValidationState.phase === 'error'
      ? 'error'
      : hasSaveErrors
        ? 'error'
      : saveValidationState.phase === 'pending' || saveValidationState.phase === 'idle'
        ? 'info'
        : hasBackendWarning || showLocalWarning
          ? 'warning'
          : null;
  const inlineAlertMessage =
    saveValidationState.phase === 'pending' || saveValidationState.phase === 'idle'
      ? 'Validando'
      : saveValidationState.phase === 'invalid'
        ? 'Bloqueado'
        : saveValidationState.phase === 'error'
          ? 'Falha validação'
          : hasSaveErrors
            ? 'Dimensões inválidas'
          : hasBackendWarning
            ? 'Atenção'
          : showLocalWarning
            ? 'Aviso'
            : null;

  return (
    <div className="bg-navy-800 border-t border-navy-700 min-w-0">
      {/* Cards de métricas */}
      <div className="p-2 border-b border-navy-700 min-w-0">
        <div className="grid grid-cols-2 gap-2 min-w-0">
          <div className="flex items-center bg-navy-700/50 rounded p-1.5 min-w-0">
            <div className="p-1 bg-emerald-500/10 rounded-full mr-1.5 flex-shrink-0">
              <Layers className="w-3.5 h-3.5 text-emerald-500" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[10px] text-gray-400 truncate">Total de Objetos</div>
              <div className="text-sm font-bold text-white">{totalObjectsWithQuantity}</div>
            </div>
          </div>

          <div className={`flex items-center rounded p-1.5 min-w-0 ${disableSoldMeter ? 'bg-navy-700/30 opacity-60 pointer-events-none' : 'bg-navy-700/50'}`}
               aria-disabled={disableSoldMeter}
          >
            <div className="p-1 bg-yellow-500/10 rounded-full mr-1.5 flex-shrink-0">
              <MoveVertical className="w-3.5 h-3.5 text-yellow-500" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[10px] text-gray-400 truncate">{isMetros ? "Metragem" : "Unidades"}</div>
              <div className="text-sm font-bold text-white">
                {disableSoldMeter ? '—' : `${metrosVendidos.toFixed(isMetros ? 2 : 0)} ${unit}`}
              </div>
            </div>
          </div>
          
          <div className={`col-span-2 flex items-center bg-navy-700/50 rounded p-1.5 ${hasExceededMeterage ? 'border border-red-500' : ''} min-w-0`}>
            <div className={`p-1 ${hasExceededMeterage ? 'bg-red-500/10' : 'bg-blue-500/10'} rounded-full mr-1.5 flex-shrink-0`}>
              <Ruler className={`w-3.5 h-3.5 ${hasExceededMeterage ? 'text-red-500' : 'text-blue-500'}`} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] text-gray-400 truncate">{isMetros ? "Total Calculado" : "Total Calculado"}</div>
              <div className="flex items-center justify-between min-w-0">
                <div className={`text-sm font-bold ${hasExceededMeterage ? 'text-red-400' : 'text-white'} truncate`}>
                  {formattedTotal.toFixed(isMetros ? 2 : 0)} {unit}
                </div>
                {hasExceededMeterage && (
                  <div className="text-xs text-red-400 ml-1 flex-shrink-0">
                    {metrageExcess.toFixed(isMetros ? 2 : 0)}{unit}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Barra de ações */}
      <div className="p-2 flex items-center justify-between min-w-0">
        {/* Informações do usuário */}
        <div className="flex items-center space-x-2 min-w-0 flex-1">
          <div className="text-[10px] min-w-0">
            <p className="text-gray-400 truncate">{userType}</p>
            <p className={`${roleColors.text} capitalize truncate`}>{userName}</p>
          </div>
        </div>

        {/* Container de ações */}
        <div className="flex items-center gap-2 flex-shrink-0">
          {/* Mensagens de erro */}
          {inlineAlertType && inlineAlertMessage && (
            <div className="flex items-center gap-1 flex-shrink-0">
              <InlineAlert
                type={inlineAlertType}
                message={inlineAlertMessage}
                className="text-[10px] px-1.5 py-0.5"
              />
            </div>
          )}

          {/* Botões de ação */}
          <div className="flex items-center gap-1 flex-shrink-0">
            {onShowOrderDetails && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onShowOrderDetails}
                icon={FileText}
                title="Detalhes do pedido (cliente, item mãe, observações, valores)"
                className="p-1 text-yellow-300 hover:bg-yellow-500/20"
              />
            )}
            {onShowDebug && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onShowDebug}
                icon={Info}
                title="Informações do Pedido (objetos e limites)"
                className="p-1 text-blue-400 hover:bg-blue-500/20"
              />
            )}
            {/* Botão Recusar – apenas produção e quando callback existir */}
            {effectiveActionRole === 'producao' && typeof onRefuse === 'function' && (
              <Button
                variant="danger"
                size="xs"
                onClick={onRefuse}
                className="min-w-[80px]"
                title="Recusar arte e solicitar correção"
              >
                Recusar
              </Button>
            )}
            <div className="group relative">
              <Button
                variant={effectiveActionRole === 'producao' ? 'warning' : 'primary'}
                size="xs"
                onClick={() => {
                  sendToTerminal('🔘 [FOOTER] Botão Salvar/Exportar CLICADO', {
                    role,
                    saveValidationPhase: saveValidationState.phase,
                    hasBlocking,
                    reorderMode,
                    isDisabled: saveDisabled,
                    totalObjectsWithQuantity
                  });
                  onAction();
                }}
                disabled={saveDisabled}
                icon={effectiveActionRole === 'producao' && !openOnlyFlow ? Download : Save}
                className="min-w-[80px]"
                title={saveTitle}
              >
                {saveLabel}
              </Button>
              {saveDisabled && (
                <div className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 px-2 py-1 bg-navy-700 text-white text-[10px] rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                  {saveTitle}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
